import { DateTime } from "luxon";
import { useBetbook } from "~/store/betbook";
import { useSettings } from "~/store/settings";
import type { IStreamConfig } from "~/types/api/streaming";
import api from "~/utils/auth/api";

export interface IBuildQueryOptions {
	streamingConfig: IStreamConfig[];
	fromEpoch: number;
	toEpoch: number;
	activeFilters?: { sport: string | null } | undefined;
}

export interface ITimeSlot {
	timeHour: number;
	fromEpoch: number;
	toEpoch: number;
	hasEvents: boolean;
}

const fetchStreamConfig = (refetch = false) => {
	const betbookStore = useBetbook();
	const isLoading = toRef(false);
	const isError = toRef(false);
	const data = toRef<{ [x: string]: { sportProvider: string; leagueId: string; sportCountry: string }[] } | undefined>(undefined);

	if (!betbookStore.streamableLeagues || refetch) {
		isLoading.value = true;
		api<IStreamConfig[]>({
			url: "/cron/sport-streaming/{brand}?api-version=2.0",
			type: "config",
			options: { method: "GET" },
		})
			.then((res) => {
				data.value = parseStreamingConfig(res);
				betbookStore.streamableLeagues = data.value;
			})
			.catch((e) => (isError.value = true))
			.finally(() => (isLoading.value = false));
	}

	return { isLoading, isError, data };
};

const parseStreamingConfig = (data: IStreamConfig[]) => {
	const structuredRes: { [x: string]: { sportProvider: string; leagueId: string; sportCountry: string }[] } = {};
	for (const item of data) {
		if (item.sportId && item.sportProvider && item.sportProvider) {
			const leagues = item.leagues.map((league) => {
				return {
					...league,
					...{ sportProvider: item.sportProvider === "Sports Radar" ? "Sportsradar" : item.sportProvider },
				};
			});
			if (!structuredRes[item.sportId]) {
				structuredRes[item.sportId] = leagues;
			} else {
				structuredRes[item.sportId] = [...structuredRes[item.sportId], ...leagues];
			}
		}
	}
	return structuredRes;
};

const flattenRegions = (data: IStreamConfig) => {
	return data.leagues.reduce<Record<string, string[]>>((result, item) => {
		if (result[item.sportCountry]) {
			result[item.sportCountry].push(item.leagueId);
		} else {
			result[item.sportCountry] = [item.leagueId];
		}
		return result;
	}, {});
};

const buildQuery = (data: IBuildQueryOptions) => {
	const settings = useSettings();
	const queryParts = [
		`countryCode=${settings.getCountryConfig?.countryIsoTwo}`,
		`cultureCode=${settings.locale}`,
		`isEsport=false`,
		`boostedOnly=false`,
		`sortBy=League`,
		`FromStartEpoch=${data.fromEpoch}`,
		`ToStartEpoch=${data.toEpoch}`,
	];
	if (data.activeFilters) {
		const sport = data.streamingConfig.find((sc) => sc.sportId === data.activeFilters?.sport);
		if (sport) {
			queryParts.push(`sportIds=${sport?.sportId}`);
			const flattenedRegion = flattenRegions(sport);
			const regions = Object.keys(flattenedRegion);
			for (const region of regions) {
				const leagues = flattenedRegion[region];
				queryParts.push(`regions=${encodeURIComponent(region)}`);
				leagues.forEach((league) => {
					queryParts.push(`leagues=${encodeURIComponent(league)}`);
				});
			}
		}
	} else {
		for (const streamableSport of data.streamingConfig) {
			queryParts.push(`sportIds=${streamableSport.sportId}`);
			const flattenedRegion = flattenRegions(streamableSport);
			const regions = Object.keys(flattenedRegion);
			for (const region of regions) {
				const leagues = flattenedRegion[region];
				queryParts.push(`regions=${encodeURIComponent(region)}`);
				leagues.forEach((league) => {
					queryParts.push(`leagues=${encodeURIComponent(league)}`);
				});
			}
		}
	}

	return "&" + queryParts.join("&");
};

const getTimes = async (day: DateTime | undefined): Promise<ITimeSlot[]> => {
	let times = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
	const availableTimes: ITimeSlot[] = [];

	return new Promise<ITimeSlot[]>((resolve, reject) => {
		if (!day) return resolve([]);

		try {
			if (day.day === DateTime.now().day) {
				const currentIndex = times.indexOf(DateTime.now().hour);
				times = times.slice(currentIndex);
			}

			for (const timeHour of times) {
				availableTimes.push({
					timeHour: timeHour,
					fromEpoch: day.set({ hour: timeHour, minute: 0, second: 0, millisecond: 0 }).toSeconds(),
					toEpoch: day.set({ hour: timeHour, minute: 59, second: 59, millisecond: 0 }).toSeconds(),
					hasEvents: false,
				});
			}

			resolve(availableTimes);
		} catch {
			reject("Unable to get times");
		}
	});
};

export { fetchStreamConfig, parseStreamingConfig, buildQuery, getTimes };
